import { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Clock, Calendar, Globe, ChevronRight, ChevronDown, Music, Pencil, Trash2, RefreshCw } from 'lucide-react';
import axios from '../api/axios';
import { isAxiosError } from 'axios';
import ConfirmModal from './ConfirmModal';
import type { Event } from '../types/Event';
import { describeCron } from '../utils/cronDescription';

export interface RecurringTemplate extends Event {
  occurrenceCount: number;
}

interface RecurringTemplatePopupProps {
  template: RecurringTemplate;
  onSave: (updated: RecurringTemplate) => void;
  onDelete: (id: string) => void;
  onOpenImagePreview?: (imageUrl: string, title: string) => void;
}

function toDateTimeLocalValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function normalizeUrl(url?: string | null) {
  if (!url) {
    return null;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `https://${url}`;
}

export default function RecurringTemplatePopup({ template, onSave, onDelete, onOpenImagePreview }: RecurringTemplatePopupProps) {
  const map = useMap();
  const popupRootRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<RecurringTemplate>(template);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isClearOccurrencesConfirmOpen, setIsClearOccurrencesConfirmOpen] = useState(false);
  const [isClearingOccurrences, setIsClearingOccurrences] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const DESCRIPTION_MAX_CHARS = 100;
  const isDescriptionTruncated = (template.description?.length ?? 0) > DESCRIPTION_MAX_CHARS;

  useEffect(() => {
    setFormData(template);
    setIsEditing(false);
    setImageFile(null);
    setImagePreview(null);
  }, [template]);

  useEffect(() => {
    const root = popupRootRef.current;
    if (!root) {
      return;
    }

    // Leaflet listens to native mouse events on map/popup containers; this prevents popup-closing side effects.
    L.DomEvent.disableClickPropagation(root);
    L.DomEvent.disableScrollPropagation(root);
  }, []);

  const getActivePopupMarker = useCallback((): L.Marker | null => {
    const popup = (map as unknown as { _popup?: { _source?: L.Marker } })._popup;
    return popup?._source ?? null;
  }, [map]);

  const reverseGeocodeLocation = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return (data?.display_name as string | undefined) ?? null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const marker = getActivePopupMarker();
    if (!marker?.dragging) {
      return;
    }

    marker.dragging.enable();

    const handleDragEnd = async () => {
      const latLng = marker.getLatLng();
      const nextLat = latLng.lat;
      const nextLng = latLng.lng;
      const nextLocation = await reverseGeocodeLocation(nextLat, nextLng);
      marker.openPopup();

      setFormData((prev) => ({
        ...prev,
        lat: nextLat,
        lng: nextLng,
        location: nextLocation || prev.location,
      }));
    };

    marker.on('dragend', handleDragEnd);

    return () => {
      marker.off('dragend', handleDragEnd);
      marker.dragging?.disable();
    };
  }, [isEditing, map, getActivePopupMarker]);

  const resetMarkerPositionToSaved = () => {
    const marker = getActivePopupMarker();
    if (!marker?.setLatLng) {
      return;
    }

    marker.setLatLng([template.lat, template.lng]);
  };

  const startEdit = () => {
    setFormData(template);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    resetMarkerPositionToSaved();
    setFormData(template);
    setIsEditing(false);
    clearImageFile();
  };

  const clearImageFile = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setFormData((prev) => ({ ...prev, imageUrl: null }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditMapMarkerDragEnd = async (e: L.LeafletEvent) => {
    const marker = e.target as L.Marker;
    const { lat, lng } = marker.getLatLng();
    setFormData((prev) => ({ ...prev, lat, lng }));
    const nextLocation = await reverseGeocodeLocation(lat, lng);
    if (nextLocation) setFormData((prev) => ({ ...prev, location: nextLocation }));
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) {
      return formData.imageUrl ?? null;
    }

    setIsUploadingImage(true);

    try {
      const payload = new FormData();
      payload.append('file', imageFile);
      payload.append('type', 'confirmed');

      const response = await axios.post('/upload', payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return (response.data?.url as string | undefined) ?? null;
    } catch (error) {
      console.error('Error uploading image to S3:', error);
      alert('Nie udało się wysłać zdjęcia do S3');
      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const uploadedImageUrl = await uploadImageToS3();

      const payload = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        lat: formData.lat,
        lng: formData.lng,
        imageUrl: uploadedImageUrl,
        url: formData.url,
        ticketUrl: formData.free ? null : formData.ticketUrl,
        free: formData.free,
        tags: (formData.tags ?? []).map((tag) => tag.trim()).filter(Boolean),
        recurring: true,
        // recurrenceCron drives materialization and is always required here;
        // recurrencePattern is only a human-readable label, so both may
        // legitimately be present at once — never strip the cron on save.
        recurrencePattern: formData.recurrencePattern || null,
        recurrenceCron: formData.recurrenceCron || null,
        recurrenceStartDate: formData.recurrenceStartDate ? new Date(formData.recurrenceStartDate).toISOString() : null,
        recurrenceEndDate: formData.recurrenceEndDate ? new Date(formData.recurrenceEndDate).toISOString() : null,
      };
      const response = await axios.put(`/events/${formData.id}`, payload);
      onSave({ ...formData, ...response.data });
      setIsEditing(false);
      clearImageFile();
    } catch (err) {
      console.error('Error updating recurring series:', err);
      const message = isAxiosError(err) ? err.response?.data?.error : undefined;
      alert(message || 'Nie udało się zapisać zmian.');
    } finally {
      setIsSaving(false);
    }
  };

  const openImagePreview = () => {
    if (!template.imageUrl || !onOpenImagePreview) {
      return;
    }

    onOpenImagePreview(template.imageUrl, template.title);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/events/${template.id}`, { params: { scope: 'series' } });
      onDelete(template.id);
    } catch (err) {
      console.error('Error deleting recurring series:', err);
      alert('Nie udało się usunąć cyklu.');
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  // Keeps the template but drops its materialized occurrences, so the next
  // scheduled expansion (up to 24h) regenerates them from the current cron.
  const handleClearOccurrences = async () => {
    setIsClearingOccurrences(true);
    try {
      await axios.delete(`/events/${template.id}`, { params: { scope: 'occurrences' } });
      onSave({ ...template, occurrenceCount: 0 });
    } catch (err) {
      console.error('Error clearing materialized occurrences:', err);
      alert('Nie udało się usunąć zmaterializowanych wystąpień.');
    } finally {
      setIsClearingOccurrences(false);
      setIsClearOccurrencesConfirmOpen(false);
    }
  };

  return (
    <>
      <div
        ref={popupRootRef}
        id="recurring-template-popup-root"
        className={`relative transition-all duration-200 ${isEditing ? 'w-[320px] max-w-[460px]' : 'w-[320px] max-w-[320px]'}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
      {isEditing ? (
        <div className="space-y-2">
          <label className="modal-label">Tytuł:</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="modal-input"
          />
          <label className="modal-label">Opis:</label>
          <textarea
            value={formData.description ?? ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            className="modal-textarea"
          />
          <label className="modal-label">Lokalizacja:</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="modal-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="modal-label">Długość geo.:</label>
              <input
                type="number"
                step="any"
                value={formData.lng}
                onChange={(e) => setFormData({ ...formData, lng: Number(e.target.value) })}
                className="modal-input"
              />
            </div>
            <div>
              <label className="modal-label">Szerokość geo.:</label>
              <input
                type="number"
                step="any"
                value={formData.lat}
                onChange={(e) => setFormData({ ...formData, lat: Number(e.target.value) })}
                className="modal-input"
              />
            </div>
          </div>
          <label className="modal-label">Lokalizacja (przeciągnij pinezkę):</label>
          <div className="h-[220px] rounded-lg overflow-hidden border border-white/20 mb-2 mt-2">
            <MapContainer center={[formData.lat, formData.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />
              <Marker
                position={[formData.lat, formData.lng]}
                draggable
                eventHandlers={{ dragend: handleEditMapMarkerDragEnd }}
              />
            </MapContainer>
          </div>
          <p className="text-xs text-slate-300">
            Przeciągnij marker cyklu na mapie — długość, szerokość i miejsce zaktualizują się automatycznie.
          </p>
          <label className="modal-label mt-2">Wydarzenie cykliczne</label>
          <label className="modal-label">Cykl obowiązuje od:</label>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(formData.recurrenceStartDate)}
            onChange={(e) => setFormData({ ...formData, recurrenceStartDate: e.target.value || null })}
            className="modal-input"
          />
          <label className="modal-label">Cykl obowiązuje do (opcjonalnie):</label>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(formData.recurrenceEndDate)}
            onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value || null })}
            className="modal-input"
          />
          <label className="modal-label">Wyrażenie cron (opcjonalnie):</label>
          <input
            type="text"
            value={formData.recurrenceCron ?? ''}
            disabled={!!formData.recurrencePattern}
            onChange={(e) => setFormData({ ...formData, recurrenceCron: e.target.value || null })}
            className="modal-input"
          />
          {formData.recurrenceCron && describeCron(formData.recurrenceCron) && (
            <p className="text-xs text-slate-300">{describeCron(formData.recurrenceCron)}</p>
          )}
          <label className="modal-label">Cykl (słowny opis, opcjonalnie):</label>
          <input
            type="text"
            value={formData.recurrencePattern ?? ''}
            disabled={!!formData.recurrenceCron}
            onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value || null })}
            className="modal-input"
          />
          <p className="text-xs text-slate-300">Podaj wyrażenie cron albo słowny opis — nie oba naraz.</p>

          <label className="modal-label">Strona internetowa:</label>
          <input
            type="text"
            value={formData.url ?? ''}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            className="modal-input"
          />
          <label className="modal-label">URL do zdjęcia poglądowego:</label>
          <input
            type="url"
            value={formData.imageUrl ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setFormData({ ...formData, imageUrl: value || null });
              if (value) clearImageFile();
            }}
            className="modal-input"
            placeholder="https://..."
            disabled={!!imageFile}
          />
          <label className="modal-label">Zdjęcie poglądowe:</label>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageFileChange}
            className="modal-input"
            disabled={!!formData.imageUrl}
          />
          {imagePreview && (
            <div className="relative mt-2 w-full h-24">
              <img src={imagePreview} alt="Preview" className="w-full h-24 object-cover rounded" />
              <button
                type="button"
                onClick={clearImageFile}
                aria-label="Usuń zdjęcie"
                className="modal-close-btn absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}
          <label className="modal-label">Tagi (przecinek jako separator):</label>
          <input
            type="text"
            value={(formData.tags ?? []).join(', ')}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map((tag) => tag.trim()) })}
            className="modal-input"
          />
          <label className="modal-label flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!formData.free}
              onChange={(e) => setFormData({ ...formData, free: e.target.checked })}
            />
            Wstęp wolny
          </label>
          {!formData.free && (
            <>
              <label className="modal-label">URL biletów:</label>
              <input
                type="text"
                value={formData.ticketUrl ?? ''}
                onChange={(e) => setFormData({ ...formData, ticketUrl: e.target.value })}
                className="modal-input"
              />
            </>
          )}
          <div className="modal-btn-row-mt">
            <button type="button" onClick={cancelEdit} disabled={isSaving || isUploadingImage} className="btn-action btn-red">
              Anuluj
            </button>
            <button type="button" onClick={handleSave} disabled={isSaving || isUploadingImage} className="btn-action btn-green">
              {isUploadingImage ? 'Wysyłanie zdjęcia...' : isSaving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div id="recurring-template-popup-header" className="mb-3 flex items-start gap-3">
            {template.imageUrl ? (
              <button
                id="recurring-template-popup-image-button"
                type="button"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  openImagePreview();
                }}
                onClick={(e) => {
                  e.stopPropagation();

                  // Keyboard activation triggers click without a pointerdown event.
                  if (e.detail === 0) {
                    openImagePreview();
                  }
                }}
                className="relative block h-14 w-14 shrink-0 cursor-zoom-in overflow-hidden rounded-full"
                aria-label="Powiększ zdjęcie cyklu"
              >
                <img
                  id="recurring-template-popup-image"
                  src={template.imageUrl}
                  alt={template.title}
                  className="h-14 w-14 rounded-full object-cover"
                />
                <span
                  id="recurring-template-popup-magnifying-glass-trigger"
                  className="pointer-events-none absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/80 bg-black/60 text-white"
                  aria-hidden="true"
                >
                  <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
                    <line x1="7" y1="1" x2="7" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
                <Music size={22} strokeWidth={2} aria-hidden="true" />
                <span className="sr-only">Brak fotografii</span>
              </span>
            )}
            <div className="min-w-0 flex-1 pt-0.5 text-left">
              <p id="recurring-template-popup-title" className="leading-tight">
                <span className="sr-only">Tytuł wydarzenia:</span>
                <span className="block text-lg font-bold text-white">{template.title}</span>
              </p>
              {template.description && (
                <div className="mt-0.5">
                  <p
                    id="recurring-template-popup-description"
                    className={`text-sm leading-5 transition-colors duration-300 ease-in-out ${isDescriptionExpanded ? 'text-slate-100' : 'text-slate-400'}`}
                  >
                    <span id="recurring-template-popup-description-label" className="sr-only">Opis:</span>
                    {isDescriptionExpanded || !isDescriptionTruncated
                      ? template.description
                      : `${template.description.slice(0, DESCRIPTION_MAX_CHARS).trimEnd()}…`}
                  </p>
                  {isDescriptionTruncated && (
                    <button
                      type="button"
                      id="recurring-template-popup-description-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDescriptionExpanded((prev) => !prev);
                      }}
                      className="mt-1 flex items-center gap-1 text-xs font-semibold text-[var(--accent)] hover:text-[var(--accent-strong)]"
                    >
                      {isDescriptionExpanded ? 'Zwiń opis' : 'Rozwiń opis'}
                      <ChevronDown
                        size={14}
                        strokeWidth={2.5}
                        className={`transition-transform duration-300 ${isDescriptionExpanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mb-3 border-t border-white/10" />

          {template.free === false && (
            <div className="mb-3">
              <span className="sep-paid-badge">🎟 Wstęp płatny</span>
              {template.ticketUrl && (
                <p className="mt-1 text-sm text-slate-300">
                  <span className="uppercase tracking-wide !font-[900] !text-base !text-slate-100">Kup bilety:</span>{' '}
                  <a
                    href={normalizeUrl(template.ticketUrl) ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="popup-yellow-link underline break-all"
                  >
                    {template.ticketUrl}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="mb-3 space-y-3">
            <div id="recurring-template-popup-location" className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                <MapPin size={16} strokeWidth={2.25} aria-hidden="true" />
              </span>
              <div className="min-w-0 text-left">
                <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Lokalizacja:</span>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${template.lat},${template.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="popup-yellow-link underline decoration-dotted text-sm font-semibold"
                >
                  {template.location}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                <Calendar size={16} strokeWidth={2.25} aria-hidden="true" />
              </span>
              <div className="min-w-0 text-left">
                <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Cykl:</span>
                <p className="text-sm font-semibold text-white">
                  {template.recurrenceCron
                    ? `Cron: ${template.recurrenceCron}`
                    : template.recurrencePattern
                      ? template.recurrencePattern
                      : 'Brak zdefiniowanego cyklu'}
                </p>
                {template.recurrenceCron && describeCron(template.recurrenceCron) && (
                  <p className="mt-0.5 text-xs text-slate-400 italic">{describeCron(template.recurrenceCron)}</p>
                )}
                <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-slate-200">
                  <Clock size={12} strokeWidth={2.5} aria-hidden="true" />
                  <span>{template.recurrenceStartDate ? new Date(template.recurrenceStartDate).toLocaleDateString() : '—'}</span>
                  <span aria-hidden="true">→</span>
                  <span>{template.recurrenceEndDate ? new Date(template.recurrenceEndDate).toLocaleDateString() : 'bez daty końcowej'}</span>
                </div>
              </div>
            </div>

            {template.url ? (
              <a
                href={normalizeUrl(template.url) ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Otwórz stronę internetową wydarzenia w nowej karcie"
                title="Otwórz stronę wydarzenia"
                className="-mx-1 flex items-center gap-2.5 rounded-lg px-1 py-0.5 hover:bg-white/5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Strona internetowa:</span>
                  <span className="popup-yellow-link block truncate text-sm font-semibold underline">{template.url}</span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
              </a>
            ) : (
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <div className="min-w-0 text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">Strona internetowa:</span>
                  <span className="text-sm text-slate-300">Brak strony internetowej</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-3 border-t border-white/10" />

          {(template.tags ?? []).length > 0 && (
            <div className="mb-3">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Tagi:</span>
              <div id="recurring-template-popup-tags-row" className="sep-tags-row mt-1">
                {(template.tags ?? []).map((tag) => (
                  <span key={tag} className="sep-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {template.free !== false && (
            <span className="sep-free-badge mb-3 inline-block">
              Wstęp wolny
            </span>
          )}

          <p className="mb-3 text-sm text-slate-300">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Zmaterializowanych wystąpień: {template.occurrenceCount}</span>
          </p>

          <div className="space-y-2">
            <button
              type="button"
              onClick={startEdit}
              className="btn-action btn-blue flex w-full items-center justify-center gap-1.5"
            >
              <Pencil size={14} strokeWidth={2.5} aria-hidden="true" />
              Edytuj
            </button>
            <button
              type="button"
              onClick={() => setIsClearOccurrencesConfirmOpen(true)}
              className="btn-action btn-amber flex w-full items-center justify-center gap-1.5"
            >
              <RefreshCw size={14} strokeWidth={2.5} aria-hidden="true" />
              Wyczyść zmaterializowane wystąpienia
            </button>
            <button
              type="button"
              onClick={() => setIsDeleteConfirmOpen(true)}
              className="btn-action btn-red flex w-full items-center justify-center gap-1.5"
            >
              <Trash2 size={14} strokeWidth={2.5} aria-hidden="true" />
              Usuń cały cykl
            </button>
          </div>
        </>
      )}

      </div>
      {isDeleteConfirmOpen && (
        <ConfirmModal
          title="Usuń cały cykl"
          message={`Czy na pewno chcesz usunąć cykl "${template.title}" i wszystkie jego wystąpienia?`}
          actions={[
            { label: 'Usuń cały cykl', variant: 'red', onClick: handleDelete },
            { label: 'Anuluj', variant: 'gray', onClick: () => setIsDeleteConfirmOpen(false) },
          ]}
          onDismiss={() => setIsDeleteConfirmOpen(false)}
        />
      )}
      {isClearOccurrencesConfirmOpen && (
        <ConfirmModal
          title="Wyczyść zmaterializowane wystąpienia"
          message={`Czy na pewno chcesz usunąć wszystkie zmaterializowane wystąpienia cyklu "${template.title}"? Sam cykl (szablon) pozostanie i nowe wystąpienia zostaną wygenerowane na podstawie aktualnego wyrażenia cron w ciągu 24 godzin.`}
          actions={[
            { label: isClearingOccurrences ? 'Usuwanie...' : 'Wyczyść wystąpienia', variant: 'red', onClick: handleClearOccurrences },
            { label: 'Anuluj', variant: 'gray', onClick: () => setIsClearOccurrencesConfirmOpen(false) },
          ]}
          onDismiss={() => setIsClearOccurrencesConfirmOpen(false)}
        />
      )}
    </>
  );
}
